<script setup lang="ts">
import type { ContentProductPackage } from '~/models/Content/ContentProductPackage'
import type { ContentProductHeader } from '~/models/Content/ContentProductHeader'
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { faArrowLeft } from '@fortawesome/duotone-regular-svg-icons'
import { firstOf } from '~/utils/queryUtils'
import { setTitle } from '~/utils/dom'
import { forceColorShade } from '~/utils/colors'
import useProductStore from '~/stores/product'
import { ContentType } from '~/models/Content/ContentType'
import { ColorTheme } from '~/models/Content/Color'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useMedia } from '~/composables/useMedia'
import { useColorTheme } from '~/composables/useColorTheme'
import useCardStyles from '~/composables/useCardStyles'
import { useAppColor } from '~/composables/useAppColor'
import useContentApi from '~/api/contentApi'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import HeaderViewBannerGrid from '~/components/utils/HeaderViewBannerGrid.vue'
import CopyrightButton from '~/components/utils/CopyrightButton.vue'
import Button from '~/components/ui/Button.vue'
import HeaderStencil from '~/components/subject/HeaderStencil.vue'
import Header8To10Section from '~/components/subject/gradetypes/Header8To10Section.vue'
import HeaderViewSkeleton from '~/components/skeletons/HeaderViewSkeleton.vue'
import NotFound from '~/components/ResourceEngine/Components/NotFound.vue'
import Image from '~/components/media/Image.vue'
import SimpleCard from '~/components/cards/SimpleCard.vue'

const {
  locationId,
  task,
} = defineProps<{
  locationId: number
  task?: Task
}>()

const { t } = useI18n()
const { findContents } = useContentApi()
const { getProductFromResource } = useProductStore()
const { resolveBaseUrl } = useUrlBuilder()
const { set: setAppColor, clear: clearAppColor } = useAppColor()
const { theme } = useColorTheme(ColorTheme.BlueOrange)
const { cardStyle, themeStyle } = useCardStyles(theme)

const { data, isLoading, isError, error, isSuccess } = useQuery({
  queryKey: computed(() => ['student_task', locationId]),
  queryFn: async () => {
    const [header, sections] = await Promise.all([
      findContents<ContentProductPackage>({
        locationIdCriterion: [locationId],
        contentTypeCriterion: [ContentType.ProductPackage],
      }, 1).then(([data]) => data),
      findContents<ContentProductHeader>({
        parentLocationIdCriterion: [locationId],
        contentTypeCriterion: [ContentType.ProductHeader],
        sortField: 'priority',
        sortOrder: 'asc',
      }),
    ])
    return { header, sections }
  },
  enabled: computed(() => !!locationId),
  staleTime: Infinity,
  retry: false,
})

const { data: selectedTasks, isLoading: isLoadingSelectedTasks } = useQuery({
  queryKey: computed(() => ['student_selected_tasks', task?.taskLocationIds]),
  queryFn: () => findContents({
    locationIdCriterion: task?.taskLocationIds ?? undefined,
  }, task?.taskLocationIds?.length),
  enabled: computed(() => !!task?.taskLocationIds?.length && isSuccess.value),
  select: (data) => {
    const ids = task?.taskLocationIds ?? []
    const byId = new Map((data).map(task => [task.locationId, task]))
    return ids.map(id => byId.get(id)).filter(Boolean)
  },
  staleTime: Infinity,
})

const mediaContentId = computed(() => Number(data?.value?.header?.image?.destinationContentId))
const product = computed(() => data.value?.header && getProductFromResource(data.value?.header))
const productLink = computed(() => product.value ? resolveBaseUrl(product.value) : '')

const headerStyle = computed(() => {
  const id = data.value?.header?.stencil?.identifier
  return (id && id !== 'none') && { background: forceColorShade(theme.value[60], '30') }
})

const sections = computed(() => {
  const sections = data.value?.sections ?? []
  return task?.taskLocationIds?.length ? sections.slice(0, 1) : sections
})

const { data: bgMedia, isLoading: bgMediaIsLoading } = firstOf(useMedia([mediaContentId.value]))

const taskResources = computed(() => (selectedTasks.value?.filter(task => task !== undefined) || []))

onMounted(() => setAppColor({
  themeColor: theme.value[60],
  bgColor: theme.value[10],
}))

onBeforeUnmount(() => clearAppColor())

watch(data, () => {
  if (!data.value?.header)
    return
  setTitle(t('termExam.byline', { title: data.value.header.title.toLowerCase() }))
}, { immediate: true })
</script>

<template>
  <div
    class="grid gap-20 text-(--theme-60)"
    :style="themeStyle"
  >
    <HeaderViewSkeleton v-if="isLoading || isLoadingSelectedTasks" />
    <NotFound
      v-else-if="isError"
      :error
      :return-path="productLink"
      class="mt-24"
    />
    <template v-else>
      <HeaderViewBannerGrid class="bg-(--theme-60) text-(--theme-10)">
        <div class="grid-in-header-info flex flex-col items-start gap-4 self-center py-24 md:py-32">
          <Button
            v-if="product && !!productLink"
            :to="{ path: productLink }"
            :color="theme[50]"
            shade="light"
            variant="outline"
            class="mb-8"
          >
            <font-awesome-icon :icon="faArrowLeft" />
            {{ product.aunivers.name }}
          </Button>
          <span
            class="tracking-wider uppercase"
            v-text="t('termExam.byline', { title: data?.header?.parentLocationName || '' })"
          />
          <h1
            class="text-4xl font-bold"
            v-text="data?.header?.title"
          />
          <RichTextRenderer
            v-if="data?.header?.intro"
            class="prose text-xl text-[currentColor]"
            :text="data.header.intro"
          />
        </div>
        <div
          class="grid-in-image md:grid-in-image-top-full relative size-full"
          :style="headerStyle"
        >
          <HeaderStencil
            v-if="data?.header?.stencil?.identifier"
            :stencil="data?.header.stencil.identifier"
            class="absolute left-0 z-10"
          />
          <Image
            v-if="mediaContentId"
            :content="mediaContentId"
            class="aspect-video !h-auto min-h-full w-full max-w-full place-self-end object-cover object-left md:h-0 "
            :class="!bgMediaIsLoading && 'animate-fade'"
          />
        </div>
        <CopyrightButton
          v-if="bgMedia && 'copyright' in bgMedia && bgMedia.copyright"
          :copyright="bgMedia.copyright"
          class="col-end-[image] row-start-2 m-4 place-self-end"
        />
      </HeaderViewBannerGrid>
      <section
        v-for="(section, index) in sections"
        :key="section.contentId"
        class="grid grid-cols-1 place-items-center last:pb-20"
        :class="{ 'bg-(--theme-20) py-20 [.py-20+&]:-mt-20 [.py-20+&]:pt-0': section.viewMode === 'task' }"
        :style="cardStyle(section)"
      >
        <div class="max-w-screen-au flex w-full flex-col gap-24 px-4 sm:px-8">
          <Header8To10Section
            v-if="data?.header"
            :header="data.header"
            :section="section"
            :index
            :grade-filter="false"
          />
        </div>
      </section>
      <section v-if="task?.taskLocationIds?.length" class="grid grid-cols-1 place-items-center pb-20">
        <div class="max-w-screen-au flex w-full flex-col gap-4 px-4 sm:px-8">
          <h2
            class="font-tiempos text-4xl"
            v-text="t('assign.tasks')"
          />
          <ul v-if="taskResources.length" class="grid auto-rows-fr gap-[1px] sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            <li
              v-for="taskResource in taskResources"
              :key="taskResource.locationId"
              class="flex"
            >
              <SimpleCard :resource="taskResource!">
                <template #task-number>
                  <span class="-mb-2 text-lg" v-text="taskResource?.parentLocationName" />
                </template>
              </SimpleCard>
            </li>
          </ul>
          <p
            v-else
            class="text-lg"
            v-text="t('assign.student.noTasksFound')"
          />
        </div>
      </section>
    </template>
  </div>
</template>
